import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import Drawer from "react-modern-drawer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import queryString from "qs";
import { LeadsFilters } from "../../index";
import { instance } from "../../../../../api/apiBase";
import { FilterSection } from "./FilterSection";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-rounded";

interface Props {
  filters?: LeadsFilters;
  setFilters: Dispatch<SetStateAction<LeadsFilters | undefined>>;
  showFilters: boolean;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  property_ids?: string[];
  unit_ids?: string[];
  onApplyFilters?: () => void;
  search?: string;
}

interface FilterOptions {
  statuses?: { status: string; count: number }[];
  properties?: { id: number; name: string; count: number }[];
  units?: { id: number; title: string; property_id: number; count: number }[];
  lead_sources?: {
    id: number;
    name: string;
    source_type: string;
    display_order: number;
    count: number;
  }[];
  stages?: { id: number; name: string; count: number }[];
  activity_actor_types?: { actor_type: string; count: number }[];
  activity_types?: { activity_type: string; count: number }[];
  actors?: { type: string; id: number | null; name: string; count: number }[];
}

const QUERY = "/portal/v1/leads/filter_options";

export const FiltersDrawer: FC<Props> = ({
  property_ids,
  unit_ids,
  filters,
  setFilters,
  setShowFilters,
  showFilters,
  onApplyFilters,
  search
}) => {
  const queryClient = useQueryClient();

  // Extract IDs for API call
  const stageIds = useMemo(
    () => filters?.stages?.map((s) => Number(s.id)),
    [filters?.stages]
  );
  const sourceIds = useMemo(
    () => filters?.leadSources?.map((ls) => Number(ls.id)),
    [filters?.leadSources]
  );
  const filterPropertyIds = useMemo(
    () => filters?.properties?.map((p) => Number(p.id)),
    [filters?.properties]
  );

  const { data: filterOptions } = useQuery({
    queryKey: [
      "LEADS_FILTER_OPTIONS",
      {
        property_ids: property_ids || filterPropertyIds,
        unit_ids,
        stage_ids: stageIds,
        source_ids: sourceIds,
        activity_types: filters?.activityTypes,
        search
      }
    ],
    queryFn: () =>
      instance
        .get(
          QUERY +
            "?" +
            queryString.stringify(
              {
                property_ids: property_ids || filterPropertyIds,
                unit_ids,
                stage_ids: stageIds?.length ? stageIds : undefined,
                source_ids: sourceIds?.length ? sourceIds : undefined,
                most_recent_activity: filters?.activityTypes?.length
                  ? { type: filters.activityTypes }
                  : undefined,
                search: search?.trim() || undefined
              },
              { arrayFormat: "brackets" }
            )
        )
        .then(({ data }: { data: FilterOptions }) => data)
  });

  useEffect(() => {
    if (showFilters) {
      queryClient.invalidateQueries({
        queryKey: ["LEADS_FILTER_OPTIONS"]
      });
      setSelectedProperties(filters?.properties || []);
      setSelectedStages(filters?.stages || []);
      setSelectedLeadSources(filters?.leadSources || []);
      setSelectedActivityTypes(filters?.activityTypes || []);
    }
  }, [showFilters, filters, queryClient]);

  const [selectedProperties, setSelectedProperties] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedStages, setSelectedStages] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedLeadSources, setSelectedLeadSources] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>(
    []
  );

  const toggleFilter = useCallback(
    (
      filterId: string,
      allOptions: { id: string; name: string }[] | undefined,
      setter: React.Dispatch<
        React.SetStateAction<{ id: string; name: string }[]>
      >
    ) => {
      setter((prev) =>
        prev.some((item) => item.id === filterId)
          ? prev.filter((item) => item.id !== filterId)
          : [
              ...prev,
              {
                id: filterId,
                name: allOptions?.find((opt) => opt.id === filterId)?.name ?? ""
              }
            ]
      );
    },
    []
  );

  const applyFilters = () => {
    setFilters({
      properties: selectedProperties,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      activityTypes: selectedActivityTypes
    });
    onApplyFilters?.();
    setShowFilters(false);
  };

  const currentFilters = useMemo(
    () => ({
      properties: selectedProperties,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      activityTypes: selectedActivityTypes
    }),
    [
      selectedProperties,
      selectedStages,
      selectedLeadSources,
      selectedActivityTypes
    ]
  );

  const hasSelectedFilters = useMemo(
    () =>
      Object.values(currentFilters).some(
        (filterArray) => filterArray.length > 0
      ),
    [currentFilters]
  );

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={showFilters}
      onClose={() => setShowFilters(false)}
      size={360}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
        <h1 className="text-xl font-bold text-content-1000">Filters</h1>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.SM}
          onClick={() => setShowFilters(false)}
          HugeIcon={Cancel01Icon}
        />
      </div>

      <div className="px-6">
        <Show if={hasSelectedFilters}>
          <div className="py-4 border-b border-alpha-40">
            <Button
              variant={ButtonVariant.TEXT}
              className="ml-2 text-base font-bold"
              onClick={() => {
                setSelectedProperties([]);
                setSelectedStages([]);
                setSelectedLeadSources([]);
                setSelectedActivityTypes([]);
              }}
            >
              Unselect all
            </Button>
          </div>
        </Show>

        <Show
          if={!Object.values(filterOptions ?? {}).some((opt) => opt?.length)}
        >
          <p className="my-1 text-sm text-content-400">No options available</p>
        </Show>

        <div className="divide-y divide-alpha-40 overflow-y-auto h-[calc(100vh-211px)] hide-scroll">
          <Show if={Boolean(filterOptions?.properties?.length)}>
            <FilterSection
              title="Property"
              options={
                filterOptions?.properties?.map((p) => ({
                  id: String(p.id),
                  name: p.name,
                  count: p.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedProperties.map(
                (property) => property.id
              )}
              onChange={(propertyId) =>
                toggleFilter(
                  propertyId,
                  filterOptions?.properties?.map((p) => ({
                    id: String(p.id),
                    name: p.name
                  })),
                  setSelectedProperties
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.stages?.length)}>
            <FilterSection
              title="Stage"
              options={
                filterOptions?.stages?.map((s) => ({
                  id: String(s.id),
                  name: s.name,
                  count: s.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedStages.map((stage) => stage.id)}
              onChange={(stageId) =>
                toggleFilter(
                  stageId,
                  filterOptions?.stages?.map((s) => ({
                    id: String(s.id),
                    name: s.name
                  })),
                  setSelectedStages
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.lead_sources?.length)}>
            <FilterSection
              title="Lead Source"
              options={
                filterOptions?.lead_sources?.map((ls) => ({
                  id: String(ls.id),
                  name: ls.name,
                  count: ls.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedLeadSources.map((ls) => ls.id)}
              onChange={(leadSourceId) =>
                toggleFilter(
                  leadSourceId,
                  filterOptions?.lead_sources?.map((ls) => ({
                    id: String(ls.id),
                    name: ls.name
                  })),
                  setSelectedLeadSources
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.activity_types?.length)}>
            <FilterSection
              title="Most recent activity"
              options={
                filterOptions?.activity_types?.map((at) => ({
                  name: at.activity_type,
                  count: at.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedActivityTypes}
              onChange={(activityType) => {
                setSelectedActivityTypes((prev) =>
                  prev.includes(activityType)
                    ? prev.filter((type) => type !== activityType)
                    : [...prev, activityType]
                );
              }}
            />
          </Show>
        </div>

        <div className="absolute bottom-0 py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={() => setShowFilters(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
